import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { fetchBlogPost, fetchBlogPosts, type BlogPost } from '@/services/api';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function BlogDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [related, setRelated] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = Number(idParam);
    if (!id) {
      navigate('/blog');
      return;
    }

    setLoading(true);
    fetchBlogPost(id)
      .then((data) => {
        setPost(data);
        return fetchBlogPosts(1, 4, data.category);
      })
      .then((res) => {
        setRelated(res.items.filter((p) => p.id !== id).slice(0, 3));
      })
      .catch(() => {
        navigate('/blog');
      })
      .finally(() => setLoading(false));
  }, [idParam, navigate]);

  useEffect(() => {
    if (!post) return;

    window.scrollTo(0, 0);

    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': `${SITE_URL}/blog/${post.id}`,
      url: `${SITE_URL}/blog/${post.id}`,
      headline: post.title,
      description: post.excerpt,
      image: post.image,
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at,
      author: {
        '@type': 'Person',
        name: post.author,
        worksFor: {
          '@type': 'Organization',
          name: 'Quanzhou Yunxin Outdoor Products Co., Ltd.',
        },
      },
      publisher: {
        '@type': 'Organization',
        name: 'YUNXIN OUTDOOR',
        url: SITE_URL,
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/blog/${post.id}`,
      },
      articleSection: post.category,
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-blog-detail';
    script.text = JSON.stringify(articleSchema);
    document.head.appendChild(script);

    document.title = `${post.title} | YUNXIN OUTDOOR News`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', (post.excerpt || '').slice(0, 160));

    return () => {
      const el = document.getElementById('schema-blog-detail');
      if (el) el.remove();
    };
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-40">
          <p className="text-stone-400">Loading article...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) return null;

  const paragraphs = (post.content || '').split('\n\n');
  const dateStr = (post.published_at && post.published_at.startsWith('20') ? post.published_at : post.created_at)?.slice(0, 10);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pb-0 overflow-hidden">
        <div className="relative w-full h-[300px] md:h-[380px]">
          {post.image ? (
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-stone-900 via-stone-800 to-stone-700" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 md:px-10 pb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {post.category}
              </span>
              <span className="text-white/60 text-xs">{dateStr}</span>
              <span className="text-white/60 text-xs">{post.read_time}</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight">
              {post.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-stone-50 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4">
          <nav className="flex items-center gap-2 text-xs text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/blog" className="hover:text-stone-900 transition-colors cursor-pointer">News Center</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-stone-900 line-clamp-1">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Author */}
              <div className="flex items-center gap-4 mb-10 pb-8 border-b border-stone-100">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-stone-900 text-white font-bold text-lg flex-shrink-0">
                  {post.author?.charAt(0) || 'A'}
                </div>
                <div>
                  <p className="text-stone-900 font-semibold text-sm">{post.author}</p>
                  <p className="text-stone-400 text-xs mt-0.5">Yunxin Outdoor Products · {dateStr}</p>
                </div>
                <div className="ml-auto flex items-center gap-2 text-stone-400 text-xs">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </div>
                  {post.read_time}
                </div>
              </div>

              {/* Excerpt */}
              {post.excerpt && (
                <p className="text-stone-600 text-lg leading-relaxed mb-8 font-light border-l-4 border-amber-500 pl-5 italic">
                  {post.excerpt}
                </p>
              )}

              {/* Body */}
              <div className="prose-content space-y-5">
                {paragraphs.map((para, idx) => {
                  if (para.startsWith('**') && para.endsWith('**') && para.split('**').length === 3) {
                    const heading = para.replace(/\*\*/g, '');
                    return (
                      <h2 key={idx} className="text-xl font-bold text-stone-900 mt-8 mb-3">
                        {heading}
                      </h2>
                    );
                  }
                  const parts = para.split(/(\*\*[^*]+\*\*)/g);
                  return (
                    <p key={idx} className="text-stone-600 text-base leading-relaxed">
                      {parts.map((part, pIdx) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                          return <strong key={pIdx} className="text-stone-900 font-semibold">{part.replace(/\*\*/g, '')}</strong>;
                        }
                        if (part.match(/^\d+\.\s/)) {
                          return <span key={pIdx} className="block ml-4">{part}</span>;
                        }
                        if (part.startsWith('- ')) {
                          return <span key={pIdx} className="block ml-4 before:content-['•'] before:mr-2 before:text-amber-500">{part.slice(2)}</span>;
                        }
                        return part;
                      })}
                    </p>
                  );
                })}
              </div>

              {/* Tags / Share */}
              <div className="mt-12 pt-8 border-t border-stone-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-stone-500 text-xs">Category:</span>
                  <span className="bg-amber-100 text-amber-700 text-xs font-medium px-3 py-1 rounded-full">
                    {post.category}
                  </span>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white px-6 py-3 rounded-full text-xs font-semibold tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Contact Us
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-mail-line" />
                  </div>
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-28 space-y-8">
                {/* Back to Blog */}
                <Link
                  to="/blog"
                  className="flex items-center gap-2 text-stone-600 hover:text-stone-900 text-sm font-medium transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full border border-stone-200 group-hover:border-stone-400 transition-colors">
                    <i className="ri-arrow-left-line text-sm" />
                  </div>
                  Back to News
                </Link>

                {/* Related Posts */}
                {related.length > 0 && (
                  <div>
                    <h3 className="text-stone-900 font-semibold text-sm uppercase tracking-wider mb-4">
                      Related Articles
                    </h3>
                    <div className="space-y-4">
                      {related.map((r) => (
                        <Link
                          key={r.id}
                          to={`/blog/${r.id}`}
                          className="block group cursor-pointer"
                        >
                          {r.image && (
                            <div className="w-full h-24 rounded-xl overflow-hidden mb-2">
                              <img
                                src={r.image}
                                alt={r.title}
                                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                          )}
                          <span className="text-amber-600 text-xs font-medium">{r.category}</span>
                          <p className="text-stone-800 text-xs font-semibold leading-snug mt-1 group-hover:text-amber-700 transition-colors line-clamp-2">
                            {r.title}
                          </p>
                          <p className="text-stone-400 text-xs mt-1">{r.published_at?.slice(0, 10) || r.created_at?.slice(0, 10)}</p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
